import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  accountExpectsPush,
  ensurePushSubscription,
  hasLocalPushSubscription,
  pushSupported
} from '../services/push.service';

const DISMISSED_KEY = 'standby_push_prompt_dismissed';

/**
 * Offers to turn notifications on for this browser, to people who plainly
 * want notifications and are not getting them here.
 *
 * Written for the standbyq.app move, where it matters most: a push
 * subscription belongs to the service worker registration on the origin that
 * created it, so not one of them survived the change of domain. Every existing
 * user arrived on the new site silently unsubscribed, and — this is the part
 * that makes a prompt necessary rather than nice — nothing about the app looks
 * any different. The queue still works. There is no error. The notifications
 * simply never come, which is the whole product, and the person has no reason
 * to suspect anything until they miss their turn.
 *
 * The condition is deliberately not "the domain changed". It is "this browser
 * has no subscription and the account has one somewhere" — which is also true
 * for someone who cleared their site data, switched browsers, or got a new
 * laptop. So this keeps earning its place long after the migration, and there
 * is no dated code to remove.
 *
 * Shown once, after a delay, and never again once dismissed. Notification
 * prompts are the most reflexively refused thing on the web and the reflex is
 * earned; a second showing converts nobody and costs trust.
 */
export const EnableNotificationsPrompt = () => {
  const { t } = useTranslation();
  const [show, setShow] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!pushSupported()) return undefined;
    if (localStorage.getItem(DISMISSED_KEY)) return undefined;
    // Someone who has actively blocked notifications for this site has already
    // answered. Asking again is nagging, and the browser would not show the
    // permission dialog a second time anyway.
    if (Notification.permission === 'denied') return undefined;

    let cancelled = false;
    let timer = 0;
    let waits = 0;

    const check = async () => {
      if (cancelled) return;

      /**
       * The cookie notice has first claim on this corner, and on the new
       * domain it shows to everyone — localStorage came across empty, so
       * consent is unanswered even for people who answered it years ago on the
       * old site. Two stacked banners on arrival is how you get both ignored.
       * So wait it out, up to a point; someone who leaves it unanswered for a
       * minute is not going to engage with a second card either.
       */
      if (!localStorage.getItem('standby_cookie_consent')) {
        if (waits++ < 30) timer = window.setTimeout(check, 2000);
        return;
      }

      if (await hasLocalPushSubscription()) return;
      if (!(await accountExpectsPush())) return;
      if (!cancelled) setShow(true);
    };

    // Late enough that it never competes with the page loading.
    timer = window.setTimeout(check, 4000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  const enable = async () => {
    setBusy(true);
    const ok = await ensurePushSubscription();
    setBusy(false);
    // Dismiss either way. If it worked there is nothing left to ask; if the
    // browser refused, asking again will not change its mind.
    localStorage.setItem(DISMISSED_KEY, ok ? 'enabled' : 'refused');
    setShow(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'dismissed');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-[16px] md:p-[24px]">
      <div className="max-w-[600px] mx-auto bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[#E5E5ED] dark:border-[rgba(255,255,255,0.08)] shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-[20px]">
        <p className="font-display text-[15px] font-bold text-base-black dark:text-white mb-[6px]">
          {t('pushPrompt.title')}
        </p>
        <p className="font-body text-[14px] text-[#374151] dark:text-[#A0A0A3] leading-relaxed mb-[16px]">
          {t('pushPrompt.body')}
        </p>
        <div className="flex items-center gap-[8px]">
          <button
            type="button"
            onClick={enable}
            disabled={busy}
            className="h-[40px] px-[20px] rounded-[32px] bg-[#1D1D1E] dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? t('pushPrompt.enabling') : t('pushPrompt.enable')}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="h-[40px] px-[20px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[13px] font-medium text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
          >
            {t('pushPrompt.notNow')}
          </button>
        </div>
      </div>
    </div>
  );
};
